import React from 'react';
import { ArrowDownIcon, ArrowUpIcon } from '@heroicons/react/20/solid';
import {
	useMarketStateDayPriceEvolutionForTimeframeQuery,
} from '@app/model/market_state/service/MarketStateSnapshotServiceHooks';
import _isEmpty from 'lodash/isEmpty';
import { MarketStateStatDateCompetitorSnapshot } from '@app/model/market_state/MarketStateStatDateCompetitorSnapshot';
import Currency from '@app/model/_common/Currency';
import {
	useMarketStateTools
} from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/common/hook/useMarketStateTools';
import {
	useMarketStateFilterInputTools
} from '@app/site/secured/rateshopper/rates/provider/useMarketStateFilterInputTools';
import AvailabilityHistoryTooltip, {
	AvailabilitySnapshot, PerRoomAvailability,
} from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/common/tooltip/AvailabilityComparisonTooltip';
import Utilities from '@util/Utilities';

const MarketStateForDayAvailabilityEvolutionIndicator: React.FunctionComponent<{
	current: MarketStateStatDateCompetitorSnapshot
	statDate: string,
	calculateDiff: (current: MarketStateStatDateCompetitorSnapshot, previous: MarketStateStatDateCompetitorSnapshot) => number,
	children?: React.ReactNode
}> = ({ current, statDate, calculateDiff, children }) => {

	const { filter } = useMarketStateFilterInputTools();
	const tools = useMarketStateTools();
	const renderFallback = () => <>{children}</>;
	const usePhysicalRoomHover = !current.isMainHotel;
	const renderDifference = (difference: number) => <div className="inline-flex cursor-pointer items-center gap-x-0.5 text-xs font-medium">
		{difference > 0
			? <ArrowUpIcon className="h-3 w-3 text-rose-500" />
			: <ArrowDownIcon className="h-3 w-3 text-emerald-500" />}
		<span className={difference > 0 ? 'text-rose-500' : 'text-emerald-500'}>{Math.abs(difference)}</span>
	</div>;
	const { data: dailyMarketStates, isLoading, isSuccess } = useMarketStateDayPriceEvolutionForTimeframeQuery(
		tools.companyHotel.id,
		statDate,
		filter);

	if (isSuccess) {
		if (_isEmpty(dailyMarketStates) || dailyMarketStates.length < 2) {
			return renderFallback();
		}

		const mainHotelSnapshots = dailyMarketStates.map(snap => snap.findDailyCompetitorSnapshot(current.competitorId))
			.filter(snap => !!snap) as MarketStateStatDateCompetitorSnapshot[];

		// Pick only the days we can meaningfully compare:
		//    • days with a non-empty `items` array, OR
		//    • days that were genuine Sold-Out
		const relevant = mainHotelSnapshots.filter(snap => {
			return snap.isAvailable() || snap.countTotalAvailability() > 0 || snap.isSoldOut();
		});

		//Still need at least two “relevant” days to show a change
		if (relevant.length < 2) {
			return renderFallback();
		}

		//Compute the overalççl difference between first and last relevant day
		const firstSnap = relevant[0];
		const lastSnap = relevant[relevant.length - 1];

		const difference = current.isGroup
			? calculateDiff(lastSnap, firstSnap)
			: lastSnap.countAvailabilityEvolutionTotal() - firstSnap.countAvailabilityEvolutionTotal();

		//If net change is zero, show nothing
		if (difference === 0 || lastSnap.isNotAvailable() || lastSnap.isEmpty() || firstSnap.isNotAvailable()) {
			return renderFallback();
		}

		//Build the data model for the tooltip
		const marketStateIds = [...new Set(dailyMarketStates.flatMap(snapshot => snapshot.marketStateIds))];
		const snapshots: AvailabilitySnapshot[] = relevant.map(snap => ({
			identity: snap.competitorId,
			creationDate: snap.getLowestItemForRoom().lowestItem.item?.creationDate,
			perRoomAvailability: current.isGroup
				? (snap.lowestItemPerHotelIdAndRoomIds
					.filter(item => !!item)
					.map(item => ({
						roomId: item.hotelId,
						roomName: item.findHotelName(tools.companyHotel),
						availability: item.isAvailable() ? 1 : 0,
						price: item.lowestItem?.item?.price ?? { amount: 0, currency: Currency.EUR },
						urlReference: item.isAvailable() || item.isLos()
							? { marketStateItemId: item.lowestItem.item.id, marketStateIds }
							: undefined,
					} as PerRoomAvailability)))
				: (snap.availabilityEvolutionItems
					.filter(item => !!item)
					.flatMap(item => {
						if (!usePhysicalRoomHover) {
							return [{
								roomId: item.roomMappingType,
								roomName: Utilities.snakeCaseToWords(item.roomMappingType),
								availability: item.availability,
								urlReference: undefined,
							} as PerRoomAvailability];
						}
						return (item.roomItems || []).map(roomItem => ({
							roomId: roomItem.roomId,
							roomName: `${Utilities.snakeCaseToWords(item.roomMappingType)} · ${roomItem.roomName}`,
							availability: roomItem.availability,
							price: roomItem.price,
							urlReference: undefined,
						} as PerRoomAvailability));
					})),
		}));

		//Finally render the arrow + tooltip trigger
		return (
			<AvailabilityHistoryTooltip
				tooltipId={`availability-tooltip-${firstSnap.competitorId}`}
				snapshots={snapshots}
			>
				<div className="inline-flex cursor-pointer items-center gap-x-1.5">
					{children}
					{renderDifference(difference)}
				</div>
			</AvailabilityHistoryTooltip>
		);
	}

	return renderFallback();


};

export default MarketStateForDayAvailabilityEvolutionIndicator;
